import { Badge, Button, Spinner } from '@cimplico/components';
import { useMemo, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import PageContainer from '../components/page-container/PageContainer';
import PageTitle from '../components/PageTitle';
import SecondaryPageActions from '../components/secondary-page-actions/SecondaryPageActions';
import AssignToCategoryConfirm from '../features/procedures/AssignToCategoryConfirm';
import RemoveFromCategoryConfirm from '../features/procedures/RemoveFromCategoryConfirm';
import ProcedureDeleteConfirmDialog from '../features/procedures/ProcedureDeleteConfirmDialog';
import ProceduresTable from '../features/procedures/ProceduresTable';
import { useProcedureTemplate } from '../queries/use-procedure-template';
import { TemplateProcedureOverview } from '../types/procedure';
import ProceduresPagePrimaryActions from '../features/procedures/ProceduresPagePrimaryAction';
import NewCategoryConfirm from '../features/procedures/NewCategoryConfirm';
import EditProcedureTemplateConfirm from '../features/procedures/EditProcedureTemplateConfirm';
import SyncActiveWorkpapersConfirm from '../features/procedures/SyncActiveWorkpapersConfirm';
import ProcedureTemplateDefaultConfirm from '../features/procedure-templates/ProcedureTemplateDefaultConfirm';
import ProcedureTemplatesDeleteConfirm from '../features/procedure-templates/ProcedureTemplatesDeleteConfirm';
import { useFirmContext } from '../providers/FirmProvider';
import ErrorSplashScreen from './ErrorSplashScreen';
import { useIsActiveSubscription } from '../hooks/use-is-active-subscription';
import { useIsFirmRole } from '../hooks/use-is-firm-role';
import { downloadProcedureTemplate } from '../features/procedures/utils';

function ProcedureTemplateOverviewPage(): JSX.Element {
    const navigate = useNavigate();
    const [primaryAction, setPrimaryAction] = useState<string | undefined>();
    const [secondaryAction, setSecondaryAction] = useState<string | undefined>();
    const [selectedProcedures, setSelectedProcedures] = useState<TemplateProcedureOverview[]>([]);
    const { id } = useParams();
    const { shortId, name: firmName } = useFirmContext();
    const { isExternalUser } = useIsFirmRole();
    const activeSubscription = useIsActiveSubscription();

    const procedureTemplateQuery = useProcedureTemplate(id);

    const removeFromCategory = useMemo(() => selectedProcedures.filter((p) => p.categoryId !== undefined), [selectedProcedures]);

    if (isExternalUser) {
        return <Navigate to={`/${shortId}/dashboard`} />;
    }

    if (procedureTemplateQuery.isLoading) {
        return (
            <div className="absolute w-full h-[var(--remaining-screen-height)]">
                <div className="absolute top-1/3 left-1/2">
                    <Spinner />
                </div>
            </div>
        );
    }

    if (procedureTemplateQuery.isError || !procedureTemplateQuery.isSuccess) {
        if (procedureTemplateQuery.error?.response?.status === 404) {
            return (
                <ErrorSplashScreen
                    errorCode={404}
                    title="Procedure template not found."
                    redirectTitle="Back to procedure templates"
                    redirectUrl={`/${shortId}/templates/procedures`}
                    leftArrow={true}
                />
            );
        }
        return (
            <ErrorSplashScreen
                title="An error has occurred."
                redirectTitle="Back to procedure templates"
                redirectUrl={`/${shortId}/templates/procedures`}
                leftArrow={true}
            />
        );
    }

    const { procedureTemplate } = procedureTemplateQuery.data;

    function redirectToNewTemplateProcedure(): void {
        navigate('new-procedure');
    }

    function redirectToProcedureTemplatesPage(): void {
        navigate('..');
    }

    function clearAction(): void {
        setPrimaryAction(undefined);
        setSecondaryAction(undefined);
    }

    function resetTable(): void {
        clearAction();
        setSelectedProcedures([]);
    }

    function handleChecked(procedure: TemplateProcedureOverview): void {
        setSelectedProcedures((prev) => {
            if (prev.find((p) => p.id === procedure.id)) {
                return prev.filter((p) => p.id !== procedure.id);
            }
            return [...prev, procedure];
        });
    }

    return (
        <PageContainer>
            <EditProcedureTemplateConfirm
                template={procedureTemplate}
                isOpen={primaryAction === 'EDIT_TEMPLATE'}
                onClose={clearAction}
                onEdit={resetTable}
            />
            <NewCategoryConfirm
                templateId={procedureTemplate.id}
                isOpen={primaryAction === 'NEW'}
                onClose={clearAction}
                onCreate={resetTable}
            />
            <AssignToCategoryConfirm
                templateId={procedureTemplate.id}
                isOpen={secondaryAction === 'ASSIGN'}
                onClose={clearAction}
                procedureIds={selectedProcedures.map((p) => p.id)}
                onAssign={resetTable}
            />
            <RemoveFromCategoryConfirm
                templateId={procedureTemplate.id}
                isOpen={secondaryAction === 'REMOVE'}
                onClose={clearAction}
                procedureIds={removeFromCategory.map((p) => p.id)}
                onRemove={resetTable}
            />
            <ProcedureDeleteConfirmDialog
                templateId={procedureTemplate.id}
                procedureIds={selectedProcedures.map((p) => p.id)}
                isOpen={secondaryAction === 'DELETE'}
                onClose={clearAction}
                onDelete={resetTable}
            />
            <SyncActiveWorkpapersConfirm
                templateIds={[procedureTemplate.id]}
                isOpen={primaryAction === 'SYNC'}
                onClose={clearAction}
                onSync={resetTable}
            />
            <ProcedureTemplateDefaultConfirm
                templateId={procedureTemplate.id}
                isOpen={primaryAction === 'SET_DEFAULT'}
                onClose={clearAction}
                onSet={resetTable}
            />
            <ProcedureTemplatesDeleteConfirm
                templates={[procedureTemplate]}
                isOpen={primaryAction === 'DELETE'}
                onClose={clearAction}
                onDelete={redirectToProcedureTemplatesPage}
            />
            {procedureTemplateQuery.isFetching && (
                <div className="pointer-events-none absolute top-[var(--navbar-height)] left-0 w-full h-[var(--remaining-screen-height)] z-50 bg-white/60">
                    <div className="absolute top-1/3 left-1/2">
                        <Spinner />
                    </div>
                </div>
            )}
            <div className="pb-4">
                <div className="text-gray-500 text-sm flex items-center gap-2 mb-2">
                    <Link to={`/${shortId}/dashboard`} className="hover:underline">
                        {firmName}
                    </Link>
                    <p> / Templates / </p>
                    <Link to={`/${shortId}/templates/procedures`} className="hover:underline">
                        Procedures
                    </Link>
                </div>
                <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="flex gap-4 items-center">
                        <PageTitle title={procedureTemplate.name} />
                        {procedureTemplate.system && <Badge color="blue" label="System" shape="squared" size="small" variant="default" />}
                        {procedureTemplate.default && <Badge color="gray" label="Default" shape="squared" size="small" variant="default" />}
                    </div>
                    {selectedProcedures.length > 0 && (
                        <SecondaryPageActions>
                            <Button
                                size="sm"
                                type="secondary"
                                label={`Remove from Categories (${removeFromCategory.length})`}
                                disabled={!activeSubscription || selectedProcedures.every((p) => p.categoryId === undefined)}
                                onClick={(): void => setSecondaryAction('REMOVE')}
                            />
                            <Button
                                size="sm"
                                type="secondary"
                                label={`Add to Category (${selectedProcedures.length})`}
                                onClick={(): void => setSecondaryAction('ASSIGN')}
                                disabled={!activeSubscription}
                            />
                            <Button
                                size="sm"
                                type="danger"
                                label={`Delete (${selectedProcedures.length})`}
                                onClick={(): void => setSecondaryAction('DELETE')}
                                disabled={!activeSubscription}
                            />
                        </SecondaryPageActions>
                    )}
                    {selectedProcedures.length === 0 && (
                        <ProceduresPagePrimaryActions
                            onSearch={console.log}
                            onSort={console.log}
                            onFilter={console.log}
                            onNewCategoryClick={(): void => setPrimaryAction('NEW')}
                            onNewProcedureClick={redirectToNewTemplateProcedure}
                            onEditTemplateClick={(): void => setPrimaryAction('EDIT_TEMPLATE')}
                            onSetDefaultClick={(): void => setPrimaryAction('SET_DEFAULT')}
                            onSyncTemplateClick={(): void => setPrimaryAction('SYNC')}
                            onDeleteTemplateClick={(): void => setPrimaryAction('DELETE')}
                            onDownloadTemplateClick={(): void => downloadProcedureTemplate(procedureTemplate)}
                            disablePrimaryButton={!activeSubscription || procedureTemplate.system}
                            disableSecondaryButton={!activeSubscription || procedureTemplate.system}
                            disableSyncButton={
                                !activeSubscription ||
                                procedureTemplate.system ||
                                new Date(procedureTemplate.updatedAt).getTime() <= new Date(procedureTemplate.lastSynced).getTime()
                            }
                            disableDefaultButton={!activeSubscription || procedureTemplate.default}
                            disableDeleteButton={!activeSubscription || procedureTemplate.system || procedureTemplate.default}
                            disableDownloadButton={!activeSubscription}
                            disabledPrimaryButtonHint={
                                procedureTemplate.system ? 'New procedures cannot be added into system templates.' : undefined
                            }
                            disabledSecondaryButtonHint={procedureTemplate.system ? 'System template cannot be edited.' : undefined}
                            disabledSyncButtonHint={
                                procedureTemplate.system
                                    ? 'System template cannot be synced.'
                                    : new Date(procedureTemplate.updatedAt).getTime() <= new Date(procedureTemplate.lastSynced).getTime()
                                    ? 'Procedure template is synced with open workpapers.'
                                    : undefined
                            }
                            disabledDefaultButtonHint={
                                procedureTemplate.default ? 'This procedure template is currently the default template.' : undefined
                            }
                            disableDeleteButtonHint={
                                procedureTemplate.system
                                    ? 'System template cannot be deleted.'
                                    : procedureTemplate.default
                                    ? 'Default template cannot be deleted.'
                                    : ''
                            }
                        />
                    )}
                </div>
                <section className="mt-4">
                    <ProceduresTable
                        procedureTemplate={procedureTemplate}
                        onChecked={handleChecked}
                        selectedProcedures={selectedProcedures.map((r) => r.id)}
                    />
                </section>
            </div>
        </PageContainer>
    );
}

export default ProcedureTemplateOverviewPage;
